import { useNavigate } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import { Clock, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Leg } from "../../utils/mapJourneysData";
import {
  serializeTrainStatsToSearchParams,
  trainStatsPayloadFromLeg,
} from "../../utils/trainStatsUrlParams";
import { getColor } from "../../utils/colorFormatter";
import { colors } from "../../utils/colors";
import {
  StatContainer,
  StatsContainer,
  StatBox,
  StatTopSection,
  StatIconWrapper,
  StatTitle,
  StatValue,
  InsightsButton,
  formatPercentage,
  getColorVariants,
} from '../Elements/SharedStatComponents';

type StatisticsProps = {
  leg: Leg;
}

function maybeRound(x: number | null) {
  if (x === null) return null;
  return Math.round(x);
}

function maybeMax(x: number | null, y: number | null) {
  if (x === null && y === null) return null;
  if (x === null) return y;
  if (y === null) return x;
  return Math.max(x, y);
}


const Statistics = ({ leg }: StatisticsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const cancellationPercentage = maybeRound(maybeMax(leg.arrDelay?.cancellationRate ?? null, leg.depDelay?.cancellationRate ?? null));
  const cancellationColor = cancellationPercentage === null ? colors.grey600 : getColor(cancellationPercentage, 'cancellationPercentage');
  const cancellationValueColor = getColorVariants(cancellationColor);
  
  const punctualityPercentage = maybeRound(leg.arrDelay?.punctuality ?? null);
  const punctualityColor = punctualityPercentage === null ? colors.grey600 : getColor(punctualityPercentage, 'punctualityPercentage');
  const punctualityValueColor = getColorVariants(punctualityColor);

  const handleSeeMoreClick = () => {
    // Send Mixpanel event
    mixpanel.track('Train Statistics Opened', {
      lineName: leg.line.name,
      originName: leg.origin.name,
      destinationName: leg.destination.name
    });
    
    const search = serializeTrainStatsToSearchParams(trainStatsPayloadFromLeg(leg)).toString();
    navigate(
      { pathname: '/train-statistics', search: `?${search}` },
      { state: { from: window.location.pathname, leg } }
    );
  };

  const hasStats = punctualityPercentage != null || cancellationPercentage != null;

  if (!hasStats) {
    return null;
  }

  return (
    <StatContainer $clickable onClick={handleSeeMoreClick}>
      <StatsContainer>
        {punctualityPercentage != null && (
          <StatBox>
            <StatTopSection>
              <StatIconWrapper $iconColor={punctualityColor}>
                <Clock size={16} />
              </StatIconWrapper>
              <StatValue $valueColor={punctualityValueColor}>
                {formatPercentage(punctualityPercentage)}
              </StatValue>
            </StatTopSection>
            <StatTitle>{t('journeyDetails.statistics.onTime')}</StatTitle>
          </StatBox>
        )}
        
        {cancellationPercentage != null && (
          <StatBox>
            <StatTopSection>
              <StatIconWrapper $iconColor={cancellationColor}>
                <XCircle size={16} />
              </StatIconWrapper>
              <StatValue $valueColor={cancellationValueColor}>
                {formatPercentage(cancellationPercentage)}
              </StatValue>
            </StatTopSection>
            <StatTitle>{t('journeyDetails.statistics.cancelled')}</StatTitle>
          </StatBox>
        )}
      </StatsContainer>
      <InsightsButton />
    </StatContainer>
  )
}

export default Statistics;
